import React, { useState, useEffect } from 'react';
import { 
  GitBranch, Clock, User, RotateCcw, Eye, 
  CheckCircle, FileText, Plus, AlertTriangle
} from 'lucide-react';

const ContentVersioningPanel = ({ walkId, walkTitle, onVersionRestore }) => {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [showVersionModal, setShowVersionModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [changeSummary, setChangeSummary] = useState('');
  const [restoring, setRestoring] = useState(false);

  useEffect(() => {
    if (walkId) {
      fetchVersions();
    }
  }, [walkId]);

  const fetchVersions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('cms_token') || 'contributor-user-token';
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cms/walks/${walkId}/versions`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setVersions(data);
      }
    } catch (error) {
      console.error('Error fetching versions:', error);
    } finally {
      setLoading(false);
    }
  };

  const createVersion = async () => {
    try {
      const token = localStorage.getItem('cms_token') || 'contributor-user-token';
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cms/walks/${walkId}/versions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ change_summary: changeSummary })
      });

      if (response.ok) {
        setShowCreateModal(false);
        setChangeSummary('');
        fetchVersions();
      }
    } catch (error) {
      console.error('Error creating version:', error);
    }
  };

  const restoreVersion = async (versionNumber) => {
    try {
      setRestoring(true);
      const token = localStorage.getItem('cms_token') || 'editor-user-token';
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cms/walks/${walkId}/versions/${versionNumber}/restore`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        fetchVersions();
        setShowVersionModal(false);
        if (onVersionRestore) {
          onVersionRestore();
        }
      }
    } catch (error) {
      console.error('Error restoring version:', error);
    } finally {
      setRestoring(false);
    }
  };

  const formatDateTime = (dateTime) => {
    return new Date(dateTime).toLocaleString();
  };

  const VersionModal = () => (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border max-w-2xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">
              Version {selectedVersion?.version_number} Details
            </h3>
            <button
              onClick={() => setShowVersionModal(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <FileText className="w-5 h-5" />
            </button>
          </div>
          
          {selectedVersion && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-700">Version:</span>
                  <span className="ml-2 text-gray-900">#{selectedVersion.version_number}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Created:</span>
                  <span className="ml-2 text-gray-900">{formatDateTime(selectedVersion.created_at)}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Author:</span>
                  <span className="ml-2 text-gray-900">{selectedVersion.created_by}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Current:</span>
                  <span className={`ml-2 ${selectedVersion.is_current ? 'text-green-600' : 'text-gray-500'}`}>
                    {selectedVersion.is_current ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>

              <div>
                <span className="font-medium text-gray-700">Change Summary:</span>
                <p className="mt-1 text-gray-900 bg-gray-50 p-3 rounded-lg">
                  {selectedVersion.change_summary}
                </p>
              </div>

              <div>
                <span className="font-medium text-gray-700">Content Preview:</span>
                <div className="mt-1 bg-gray-50 p-3 rounded-lg max-h-40 overflow-y-auto">
                  <p className="font-medium">{selectedVersion.title}</p>
                  <p className="text-sm text-gray-600 mt-1">{selectedVersion.description?.substring(0, 200)}...</p>
                </div>
              </div>

              {!selectedVersion.is_current && (
                <div className="flex justify-end space-x-3 pt-4 border-t">
                  <button
                    onClick={() => setShowVersionModal(false)}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => restoreVersion(selectedVersion.version_number)}
                    disabled={restoring}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center"
                  >
                    {restoring ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    ) : (
                      <RotateCcw className="w-4 h-4 mr-2" />
                    )}
                    Restore Version
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const CreateVersionModal = () => (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Create New Version</h3>
            <button
              onClick={() => setShowCreateModal(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <FileText className="w-5 h-5" />
            </button>
          </div>
          
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-600 mb-2">
                Walk: <span className="font-medium">{walkTitle}</span>
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Change Summary *
              </label>
              <textarea
                value={changeSummary}
                onChange={(e) => setChangeSummary(e.target.value)}
                rows={3}
                className="w-full rounded-md border-gray-300 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Describe what changes were made in this version..."
                required
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={() => setShowCreateModal(false)}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={createVersion}
              disabled={!changeSummary.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              Create Version
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  if (!walkId) {
    return (
      <div className="bg-gray-50 rounded-lg p-6 text-center">
        <GitBranch className="w-8 h-8 text-gray-400 mx-auto mb-2" />
        <p className="text-gray-600">Select a walk to view version history</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <GitBranch className="w-5 h-5 text-blue-600 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">Version History</h3>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center text-sm"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Version
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading version history...</p>
          </div>
        ) : versions.length === 0 ? (
          <div className="text-center py-8">
            <GitBranch className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h4 className="text-lg font-medium text-gray-900 mb-2">No Version History</h4>
            <p className="text-gray-500 mb-4">Create versions to track changes and enable rollbacks.</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              Create First Version
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {versions.map((version) => (
              <div
                key={version.id}
                className={`border rounded-lg p-4 hover:border-blue-300 transition-colors cursor-pointer ${
                  version.is_current ? 'border-green-300 bg-green-50' : 'border-gray-200'
                }`}
                onClick={() => {
                  setSelectedVersion(version);
                  setShowVersionModal(true);
                }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      version.is_current ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-700'
                    }`}>
                      {version.version_number}
                    </div>
                    <div className="ml-3">
                      <div className="flex items-center">
                        <p className="text-sm font-medium text-gray-900">
                          Version {version.version_number}
                        </p>
                        {version.is_current && (
                          <CheckCircle className="w-4 h-4 text-green-500 ml-2" />
                        )}
                        {version.is_published && (
                          <span className="ml-2 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                            Published
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">{version.change_summary}</p>
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <div className="flex items-center text-xs text-gray-500">
                      <User className="w-3 h-3 mr-1" />
                      {version.created_by}
                    </div>
                    <div className="flex items-center text-xs text-gray-500 mt-1">
                      <Clock className="w-3 h-3 mr-1" />
                      {formatDateTime(version.created_at)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Version Stats */}
        {versions.length > 0 && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-lg font-semibold text-gray-900">{versions.length}</div>
                <div className="text-xs text-gray-500">Total Versions</div>
              </div>
              <div>
                <div className="text-lg font-semibold text-gray-900">
                  {versions.filter(v => v.is_published).length}
                </div>
                <div className="text-xs text-gray-500">Published</div>
              </div>
              <div>
                <div className="text-lg font-semibold text-gray-900">
                  {versions.find(v => v.is_current)?.version_number || 'N/A'}
                </div>
                <div className="text-xs text-gray-500">Current Version</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showVersionModal && <VersionModal />}
      {showCreateModal && <CreateVersionModal />}
    </div>
  );
};

export default ContentVersioningPanel;